// ============================================================================
// OBT F2-B — Dinamik cevap alanı (tek snapshot sorusu)
// ============================================================================
// Soru tipleri backend snapshot'ından gelir: boolean/text/single_select/
// multi_select/photo/document. Backend'de olmayan tip render edilmez; koşullu
// (dallanan) soru backend'de yoktur → burada da yoktur.
//
// `photo` ve `photo_required` fotoğrafları RESPONSE seviyesinde tutulur (backend
// answer_id bağlaması istemciye açık değil — bkz. ParkResponseForm notu). Bu
// alan yalnız metin/seçim cevabını yönetir; fotoğraf bölümü form düzeyindedir.
// ============================================================================

import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Camera, FileWarning } from 'lucide-react';
import {
  QUESTION_TYPE,
  questionNeedsOptions,
  isDocumentQuestion,
  isPhotoQuestion,
  DOCUMENT_UNSUPPORTED_NOTE,
} from './responseEnums';

function RequiredMark({ required }) {
  if (!required) return null;
  return (
    <span className="text-red-600" aria-hidden="true">
      {' '}
      *
    </span>
  );
}

/**
 * @param {object} props
 * @param {object} props.question  snapshot sorusu
 * @param {object} props.answer    {answer_boolean?, answer_text?, option_values?}
 * @param {(patch: object) => void} props.onChange
 * @param {boolean} [props.readOnly]
 */
export default function DynamicAnswerField({ question, answer = {}, onChange, readOnly = false }) {
  const type = question.question_type;
  const fieldId = `q-${question.question_id}`;
  const options = Array.isArray(question.options) ? question.options : [];

  const label = (
    <Label htmlFor={fieldId} className="text-sm font-semibold text-gray-900">
      {question.question_text}
      <RequiredMark required={question.is_required} />
    </Label>
  );

  // document — cevaplanamaz (upload ucu yok).
  if (isDocumentQuestion(type)) {
    return (
      <fieldset className="space-y-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
        {label}
        <p role="note" className="flex items-start gap-1.5 text-xs text-amber-800">
          <FileWarning className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
          {DOCUMENT_UNSUPPORTED_NOTE}
        </p>
      </fieldset>
    );
  }

  // photo tipi — cevap fotoğrafla verilir (form düzeyindeki fotoğraf bölümü).
  if (isPhotoQuestion(type)) {
    return (
      <fieldset className="space-y-2 rounded-lg border border-gray-200 p-3">
        {label}
        {question.help_text && (
          <p className="text-xs text-gray-500">{question.help_text}</p>
        )}
        <Textarea
          id={fieldId}
          value={answer.answer_text || ''}
          disabled={readOnly}
          placeholder="İsteğe bağlı açıklama"
          onChange={(e) => onChange({ answer_text: e.target.value })}
        />
        <p className="flex items-center gap-1.5 text-xs text-gray-600">
          <Camera className="h-4 w-4" aria-hidden="true" />
          Bu soru için aşağıdaki “Fotoğraflar” bölümünden görsel ekleyin.
        </p>
      </fieldset>
    );
  }

  return (
    <fieldset className="space-y-2 rounded-lg border border-gray-200 p-3">
      {label}

      {type === QUESTION_TYPE.BOOLEAN && (
        <RadioGroup
          value={
            answer.answer_boolean === true
              ? 'true'
              : answer.answer_boolean === false
              ? 'false'
              : ''
          }
          disabled={readOnly}
          onValueChange={(v) => onChange({ answer_boolean: v === 'true' })}
          className="flex gap-6"
        >
          <label className="flex items-center gap-2 text-sm">
            <RadioGroupItem value="true" id={`${fieldId}-yes`} aria-label="Evet" />
            Evet
          </label>
          <label className="flex items-center gap-2 text-sm">
            <RadioGroupItem value="false" id={`${fieldId}-no`} aria-label="Hayır" />
            Hayır
          </label>
        </RadioGroup>
      )}

      {type === QUESTION_TYPE.TEXT && (
        <Textarea
          id={fieldId}
          value={answer.answer_text || ''}
          disabled={readOnly}
          maxLength={4000}
          placeholder="Cevabınızı yazın"
          onChange={(e) => onChange({ answer_text: e.target.value })}
        />
      )}

      {type === QUESTION_TYPE.SINGLE_SELECT && (
        <RadioGroup
          value={(answer.option_values && answer.option_values[0]) || ''}
          disabled={readOnly}
          onValueChange={(v) => onChange({ option_values: [v] })}
          className="space-y-1.5"
        >
          {options.map((o) => (
            <label key={o.option_value} className="flex items-center gap-2 text-sm">
              <RadioGroupItem
                value={o.option_value}
                id={`${fieldId}-${o.option_value}`}
                aria-label={o.option_label || o.option_value}
              />
              {o.option_label || o.option_value}
            </label>
          ))}
        </RadioGroup>
      )}

      {type === QUESTION_TYPE.MULTI_SELECT && (
        <div className="space-y-1.5">
          {options.map((o) => {
            const selected = Array.isArray(answer.option_values)
              ? answer.option_values.includes(o.option_value)
              : false;
            return (
              <label key={o.option_value} className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={selected}
                  disabled={readOnly}
                  aria-label={o.option_label || o.option_value}
                  onCheckedChange={(v) => {
                    const cur = Array.isArray(answer.option_values)
                      ? answer.option_values
                      : [];
                    const next = v
                      ? Array.from(new Set([...cur, o.option_value]))
                      : cur.filter((x) => x !== o.option_value);
                    onChange({ option_values: next });
                  }}
                />
                {o.option_label || o.option_value}
              </label>
            );
          })}
        </div>
      )}

      {!questionNeedsOptions(type) && type !== QUESTION_TYPE.BOOLEAN && type !== QUESTION_TYPE.TEXT && (
        // Bilinmeyen bir tip gelirse (ileride) formu çökertme; nötr bilgi ver.
        <p className="text-xs text-gray-500">Bu soru tipi görüntülenemiyor.</p>
      )}
    </fieldset>
  );
}
